import ApplePayShippingMethod = ApplePayJS.ApplePayShippingMethod
import { ApplePayConfiguration, AddressData } from "@adyen/adyen-web"
import { Cart, Address } from "@commercetools/platform-sdk"
import React, { useRef, useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useApplePayPaymentStrategy } from "../adyen/AdyenPaymentStrategies"
import { useGetShippingMethodsQuery } from "../app/api/[store]/shipping-methods/rtk-query"
import { CartCalculator } from "../cart/CartCalculator"
import { useCartService } from "../cart/CartServiceHook"
import { createProcessingLink } from "../components/Links"
import { useStoreContext } from "../i18n/StoreHooks"
import {
  getCheckoutLineItems,
  transformAppleEventToCartInfo
} from "../utils/ApplePayUtils"
import { AdyenBox } from "./payment/PaymentButtons"
import { useAdyenPaymentMethod } from "./payment/validation"
import { useCheckoutReviewValidation } from "./review/ReviewValidation"
import { shippingMethodToDurationText } from "./shipping/ShippingMethods"

const PAYMENT_TOTAL_LABEL_TEXT = "66°NORTH"
const CHECKOUT_SHIPPING_CONTACT_FIELDS: ApplePayContactField[] = [
  "name",
  "phone",
  "email",
  "postalAddress"
]
const CHECKOUT_BILLING_CONTACT_FIELDS: ApplePayContactField[] = [
  "postalAddress"
]
import "./apple-pay.css"
import ApplePayContactField = ApplePayJS.ApplePayContactField

function adyenAddressDataToCommerceTools(
  addressData: Partial<AddressData>,
  overrides?: Partial<Address>
): Address {
  const { country } = addressData
  return {
    firstName: addressData.firstName,
    lastName: addressData.lastName,
    streetName: addressData.street,
    additionalStreetInfo: addressData.houseNumberOrName,
    postalCode: addressData.postalCode,
    state: country === "US" ? addressData.stateOrProvince : undefined,
    region: country === "CA" ? addressData.stateOrProvince : undefined,
    city: addressData.city,
    country: country!,
    ...(overrides || {})
  }
}

export const ApplePayExpress: React.FC<{
  cart?: Cart
}> = ({ cart }) => {
  const { t } = useTranslation("checkout")
  const paymentContainer = useRef<HTMLDivElement | null>(null)
  const showTaxes = true

  const store = useStoreContext()
  const supportedCountries = store.supportedCountries()

  const { data: shippingMethodsForStore } = useGetShippingMethodsQuery("uk")
  const cartService = useCartService()

  const adyenMethod = useAdyenPaymentMethod([])

  const formValidation = useCheckoutReviewValidation()

  const applePayPaymentStrategy = useApplePayPaymentStrategy(
    paymentContainer,
    () => {
      if (!cart) return Promise.reject(undefined)

      return formValidation
        .validate(cart)
        .then(() => createProcessingLink(cart.id || ""))
    }
  )

  const [appleShippingMethods, setAppleShippingMethods] = useState<
    ApplePayShippingMethod[]
  >([])

  useEffect(() => {
    const cartContentsPrice =
      (cart?.totalPrice?.centAmount || 0) -
      (cart?.shippingInfo?.price.centAmount || 0)
    if (shippingMethodsForStore && shippingMethodsForStore.length > 0) {
      const transformedShippingMethods = shippingMethodsForStore.map(method => {
        const isFreeShipping = cartContentsPrice > (method.rate?.freeAbove || 0)
        return {
          label: method.name,
          identifier: method.id,
          detail: shippingMethodToDurationText(t)(method.key),
          amount:
            isFreeShipping || !method.rate
              ? "0.00"
              : store.amountByCurrencyForApplePay(method.rate)
        }
      })
      setAppleShippingMethods(transformedShippingMethods)
    }
  }, [shippingMethodsForStore])

  useEffect(() => {
    if (!cart) return
    adyenMethod.initialise(cart)
  }, [cart])

  useEffect(() => {
    let ignore = false

    if (!(paymentContainer.current && cart)) {
      // initiateCheckout is not finished yet.
      return
    }

    // The 'ignore' flag is used to avoid double re-rendering caused by React 18 StrictMode
    // More about it here: https://beta.reactjs.org/learn/synchronizing-with-effects#fetching-data

    if (!ignore && appleShippingMethods.length > 0) {
      const lineItems = getCheckoutLineItems(cart, store.currency, showTaxes, t)
      const getCheckoutTotal = (c?: Cart) => ({
        label: PAYMENT_TOTAL_LABEL_TEXT,
        amount: store.amountByCurrencyForApplePay(
          CartCalculator.totalPriceCentAmount(c)
        )
      })
      let shouldInitShippingMethod = true
      const options: ApplePayConfiguration = {
        buttonType: "buy",
        buttonColor: "white-with-line",
        requiredBillingContactFields: CHECKOUT_SHIPPING_CONTACT_FIELDS,
        requiredShippingContactFields: CHECKOUT_BILLING_CONTACT_FIELDS,
        onShippingContactSelected: async (resolve, reject, event) => {
          const { shippingAddress, emailAddress } =
            transformAppleEventToCartInfo(event)
          const { country } = shippingAddress
          const isSupportedCountry = supportedCountries.find(
            c => c.code === country
          )

          if (!isSupportedCountry) {
            resolve({
              newTotal: getCheckoutTotal(cart),
              errors: [
                new ApplePayError(
                  "shippingContactInvalid",
                  "countryCode",
                  "Cannot ship to the selected address"
                )
              ]
            })
            return
          }
          cartService
            .update({
              customerEmail: emailAddress,
              shippingAddress
            })
            .then(updatedCart => {
              return shouldInitShippingMethod
                ? cartService
                    .setShippingMethodId(appleShippingMethods[0].identifier)
                    .then(d => {
                      shouldInitShippingMethod = false
                      return d
                    })
                : updatedCart
            })
            .then(updatedCart => {
              const newTotal = getCheckoutTotal(updatedCart)

              const newLineItems = getCheckoutLineItems(
                updatedCart,
                store.currency,
                showTaxes,
                t
              )
              const newShippingMethods = appleShippingMethods
              resolve({
                newTotal,
                newLineItems,
                newShippingMethods
              })
            })
        },
        onShippingMethodSelected: (resolve, reject, event) => {
          const {
            shippingMethod: { identifier }
          } = event

          if (identifier) {
            cartService.setShippingMethodId(identifier).then(updatedCart => {
              shouldInitShippingMethod = false
              resolve({
                newTotal: getCheckoutTotal(updatedCart),
                newLineItems: lineItems
              })
            })
          }
        },
        onAuthorized: (
          { authorizedEvent, billingAddress, deliveryAddress },
          actions
        ) => {
          if (deliveryAddress && billingAddress) {
            cartService
              .update({
                customerEmail:
                  authorizedEvent.payment.shippingContact?.emailAddress,
                shippingAddress: adyenAddressDataToCommerceTools(
                  deliveryAddress,
                  {
                    phone: authorizedEvent.payment.shippingContact?.phoneNumber
                  }
                ),
                billingAddress: adyenAddressDataToCommerceTools(
                  billingAddress,
                  {
                    phone: authorizedEvent.payment.billingContact?.phoneNumber
                  }
                )
              })
              .then(actions.resolve)
          } else {
            actions.reject()
          }
        },
        isExpress: true,
        expressPage: "pdp"
      }

      applePayPaymentStrategy.createCheckout(options)
    }

    return () => {
      ignore = true
    }
  }, [applePayPaymentStrategy, appleShippingMethods])

  return (
    <React.Fragment>
      <AdyenBox
        paymentContainer={paymentContainer}
        disableCheckout={false}
        selectedPaymentType={"applepay"}
      />
    </React.Fragment>
  )
}
